import { type FC, useEffect, useState, type FormEvent } from 'react';
import apiTokensApi, {
  type ApiTokenSummary,
  type IssuedApiToken,
} from '@/api/apiTokens.api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

export const ApiTokensPage: FC = () => {
  const [tokens, setTokens] = useState<ApiTokenSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<IssuedApiToken | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      setTokens(await apiTokensApi.list());
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const issued = await apiTokensApi.create(name.trim());
      setJustCreated(issued);
      setName('');
      setTokens((prev) => [
        {
          id: issued.id,
          name: issued.name,
          prefix: issued.prefix,
          lastUsedAt: issued.lastUsedAt,
          expiresAt: issued.expiresAt,
          createdAt: issued.createdAt,
        },
        ...prev,
      ]);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    setRevoking(true);
    try {
      await apiTokensApi.revoke(revokeId);
      setTokens((prev) => prev.filter((t) => t.id !== revokeId));
    } finally {
      setRevoking(false);
      setRevokeId(null);
    }
  };

  const revokeTarget = tokens.find((t) => t.id === revokeId);

  const copy = (txt: string) => {
    navigator.clipboard?.writeText(txt).catch(() => {});
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          marginBottom: 'var(--spacing-sm)',
        }}
      >
        Jetons API
      </h1>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--spacing-lg)',
        }}
      >
        Les jetons permettent aux connecteurs externes (Claude.ai, ChatGPT, scripts) d'accéder à StratMap en votre nom.
        Le jeton complet n'est affiché qu'à la création — copiez-le immédiatement.
      </p>

      {justCreated && (
        <div
          style={{
            backgroundColor: '#10B98111',
            border: '1px solid #10B981',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Jeton créé — copiez-le maintenant :</div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              backgroundColor: 'var(--color-background)',
              borderRadius: 'var(--radius-md)',
              padding: '8px 12px',
              fontFamily: 'monospace',
              fontSize: 'var(--font-size-sm)',
              overflow: 'auto',
            }}
          >
            <span style={{ flex: 1, wordBreak: 'break-all' }}>{justCreated.token}</span>
            <Button size="sm" variant="secondary" onClick={() => copy(justCreated.token)}>
              Copier
            </Button>
          </div>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button size="sm" variant="ghost" onClick={() => setJustCreated(null)}>
              J'ai copié, masquer
            </Button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleCreate}
        style={{
          display: 'flex',
          gap: 'var(--spacing-sm)',
          alignItems: 'flex-end',
          marginBottom: 'var(--spacing-xl)',
        }}
      >
        <Input
          label="Nom du jeton"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex : Claude.ai connector"
          required
          style={{ flex: 1 }}
        />
        <Button type="submit" isLoading={creating} disabled={!name.trim()}>
          Créer un jeton
        </Button>
      </form>

      <h2
        style={{
          fontSize: 'var(--font-size-md)',
          fontWeight: 600,
          marginBottom: 'var(--spacing-sm)',
        }}
      >
        Jetons actifs
      </h2>

      {loading && (
        <p style={{ color: 'var(--color-text-muted)' }}>Chargement…</p>
      )}

      {!loading && tokens.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>Aucun jeton pour l'instant.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {tokens.map((t) => (
          <div
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{t.name}</div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  fontFamily: 'monospace',
                }}
              >
                {t.prefix}…
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginTop: 2,
                }}
              >
                Créé {new Date(t.createdAt).toLocaleDateString('fr-FR')} ·{' '}
                {t.lastUsedAt
                  ? `utilisé ${new Date(t.lastUsedAt).toLocaleDateString('fr-FR')}`
                  : 'jamais utilisé'}
              </div>
            </div>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setRevokeId(t.id)}
            >
              Révoquer
            </Button>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={revokeId !== null}
        title="Révoquer ce jeton ?"
        message={
          <p>
            Le jeton <strong>{revokeTarget?.name ?? ''}</strong> ne pourra plus servir. Les
            connecteurs qui l'utilisent devront être reconfigurés.
          </p>
        }
        confirmLabel="Révoquer"
        destructive
        busy={revoking}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
};

export default ApiTokensPage;
